import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { MetricInfo } from './MetricInfo';
import { 
  MessageSquareText, Plus, RefreshCw, Share2, Star, 
  CheckCircle, XCircle, Edit3, Trash2, Loader2 
} from 'lucide-react';

const API = window.location.origin;

export const FeedbackTab = ({ token, onCreateReview, onEditReview }) => {
  const [feedbackData, setFeedbackData] = useState({ feedback: [], stats: null });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [newReview, setNewReview] = useState({
    customer_name: '',
    overall_rating: 5,
    quality_rating: 5,
    communication_rating: 5,
    timeliness_rating: 5,
    recommend: true,
    comments: '',
    source: 'Manual',
    review_date: new Date().toISOString().split('T')[0]
  });
  const [isCreating, setIsCreating] = useState(false);

  // Fetch feedback
  const fetchFeedback = async () => {
    if (!token) return;
    setFeedbackLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackData({
        feedback: response.data.feedback || [],
        stats: response.data.stats || null
      });
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setFeedbackLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Helper for auth headers
  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // Toggle approval
  const toggleFeedbackApproved = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/api/admin/feedback/${id}`, { approved: !currentStatus }, getAuthHeaders());
      toast.success(currentStatus ? "Review hidden from public" : "Review approved and visible");
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to update feedback");
    }
  };

  // Toggle featured
  const toggleFeedbackFeatured = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/api/admin/feedback/${id}`, { featured: !currentStatus }, getAuthHeaders());
      toast.success(currentStatus ? "Removed from featured" : "Review featured on homepage");
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to update feedback");
    }
  };

  // Delete feedback
  const deleteFeedback = async (id) => {
    if (!confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/api/admin/feedback/${id}`, getAuthHeaders());
      toast.success("Review deleted");
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to delete feedback");
    }
  };

  // Create manual review
  const handleCreateReview = async () => {
    if (!newReview.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setIsCreating(true);
    try {
      await axios.post(`${API}/api/admin/feedback/manual`, {
        customer_name: newReview.customer_name,
        overall_rating: newReview.overall_rating,
        comments: newReview.comments,
        source: newReview.source,
        review_date: newReview.review_date || null
      }, getAuthHeaders());
      toast.success("Review added successfully!");
      setIsCreateDialogOpen(false);
      setNewReview({
        customer_name: '',
        overall_rating: 5,
        quality_rating: 5,
        communication_rating: 5,
        timeliness_rating: 5,
        recommend: true,
        comments: '',
        source: 'Manual',
        review_date: new Date().toISOString().split('T')[0]
      });
      fetchFeedback();
    } catch (error) {
      console.error("Error creating review:", error);
      toast.error(error.response?.data?.detail || "Failed to create review");
    } finally {
      setIsCreating(false);
    }
  };

  // Handle button click - always use internal create dialog
  const handleAddReviewClick = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEditReviewClick = (fb) => {
    const reviewDate = fb.review_date || (fb.created_at ? fb.created_at.split('T')[0] : '');
    setEditingReview({
      ...fb,
      customer_name: fb.customer_name || '',
      overall_rating: fb.overall_rating || 5,
      quality_rating: fb.quality_rating || 5,
      communication_rating: fb.communication_rating || 5,
      timeliness_rating: fb.timeliness_rating || 5,
      comments: fb.comments || '',
      improvement_suggestions: fb.improvement_suggestions || '',
      recommend: fb.recommend !== false,
      review_date: reviewDate
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    try {
      await axios.put(`${API}/api/admin/feedback/${editingReview.id}`, {
        customer_name: editingReview.customer_name,
        overall_rating: editingReview.overall_rating,
        quality_rating: editingReview.quality_rating,
        communication_rating: editingReview.communication_rating,
        timeliness_rating: editingReview.timeliness_rating,
        comments: editingReview.comments,
        improvement_suggestions: editingReview.improvement_suggestions,
        recommend: editingReview.recommend,
        review_date: editingReview.review_date || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Review updated");
      setIsEditDialogOpen(false);
      setEditingReview(null);
      fetchFeedback();
    } catch (err) {
      toast.error("Failed to update review");
    }
  };

  return (
    <>
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="font-heading flex items-center gap-2">
              <MessageSquareText className="h-5 w-5" />
              Customer Feedback
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                onClick={handleAddReviewClick}
                size="sm"
                className="gap-2"
                data-testid="add-review-btn"
              >
                <Plus className="h-4 w-4" />
                Add Review
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchFeedback}
                disabled={feedbackLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${feedbackLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const url = `${window.location.origin}/feedback`;
                  navigator.clipboard.writeText(url);
                  toast.success("Feedback link copied to clipboard!");
                }}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {feedbackLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Stats */}
              {feedbackData.stats && feedbackData.stats.total > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="bg-primary/5 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold">{feedbackData.stats.total}</p>
                    <p className="text-xs text-muted-foreground">Total Reviews <MetricInfo metricKey="reviews_total" /></p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{feedbackData.stats.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending Approval</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold flex items-center justify-center gap-1">
                      {feedbackData.stats.avg_overall}
                      <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    </p>
                    <p className="text-xs text-muted-foreground">Avg Rating</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold">{feedbackData.stats.avg_quality}</p>
                    <p className="text-xs text-muted-foreground">Quality</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold">{feedbackData.stats.avg_communication}</p>
                    <p className="text-xs text-muted-foreground">Communication</p>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold">{feedbackData.stats.recommend_percentage}%</p>
                    <p className="text-xs text-muted-foreground">Would Recommend</p>
                  </div>
                </div>
              )}

              {/* Feedback List */}
              {feedbackData.feedback?.length > 0 ? (
                <div className="space-y-4">
                  {feedbackData.feedback.map((fb) => (
                    <div key={fb.id} className={`border rounded-lg p-4 ${fb.featured ? 'border-yellow-300 bg-yellow-50/30' : !fb.approved ? 'border-orange-200 bg-orange-50/30' : ''}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-semibold">{fb.customer_name}</span>
                            {/* Source Badge */}
                            {fb.source === 'facebook' && (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200">Facebook</Badge>
                            )}
                            {fb.source === 'google' && (
                              <Badge className="bg-red-100 text-red-800 border-red-200">Google</Badge>
                            )}
                            {fb.source === 'manual' && (
                              <Badge className="bg-purple-100 text-purple-800 border-purple-200">Manual</Badge>
                            )}
                            {(!fb.source || fb.source === 'website') && (
                              <Badge className="bg-gray-100 text-gray-800 border-gray-200">Website</Badge>
                            )}
                            {!fb.approved && (
                              <Badge className="bg-orange-100 text-orange-800 border-orange-200">Pending Approval</Badge>
                            )}
                            {fb.approved && !fb.featured && (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />Approved
                              </Badge>
                            )}
                            {fb.featured && (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                <Star className="h-3 w-3 mr-1 fill-yellow-500" />Featured
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span className="capitalize">{fb.service_type?.replace('-', ' ')}</span>
                            <span>•</span>
                            <span>{new Date(fb.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-3">
                            {[1,2,3,4,5].map((star) => (
                              <Star 
                                key={star} 
                                className={`h-4 w-4 ${star <= fb.overall_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                            <span className="text-sm text-muted-foreground ml-2">({fb.overall_rating}/5)</span>
                          </div>
                          {fb.comments && (
                            <p className="text-sm mb-2">&ldquo;{fb.comments}&rdquo;</p>
                          )}
                          {fb.improvement_suggestions && (
                            <p className="text-xs text-muted-foreground italic">
                              Suggestion: {fb.improvement_suggestions}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            variant={fb.approved ? "outline" : "default"}
                            size="sm"
                            onClick={() => toggleFeedbackApproved(fb.id, fb.approved)}
                            className={!fb.approved ? 'bg-green-600 hover:bg-green-700' : ''}
                          >
                            {fb.approved ? (
                              <><XCircle className="h-4 w-4 mr-1" />Hide</>
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-1" />Approve</>
                            )}
                          </Button>
                          {fb.approved && (
                            <Button
                              variant={fb.featured ? "secondary" : "outline"}
                              size="sm"
                              onClick={() => toggleFeedbackFeatured(fb.id, fb.featured)}
                              title={fb.featured ? "Remove from featured" : "Feature on homepage"}
                            >
                              <Star className={`h-4 w-4 ${fb.featured ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditReviewClick(fb)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Edit review"
                            data-testid={`edit-feedback-${fb.id}`}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteFeedback(fb.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquareText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No feedback received yet</p>
                  <p className="text-sm text-muted-foreground">
                    Share this link with customers after completing a job:
                  </p>
                  <code className="text-sm bg-secondary px-3 py-1 rounded mt-2 inline-block">
                    {window.location.origin}/feedback
                  </code>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Edit Feedback Dialog */}
    <Dialog open={isEditDialogOpen} onOpenChange={(open) => { if (!open) { setIsEditDialogOpen(false); setEditingReview(null); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Review</DialogTitle>
        </DialogHeader>
        {editingReview && (
          <div className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input 
                value={editingReview.customer_name} 
                onChange={e => setEditingReview(prev => ({ ...prev, customer_name: e.target.value }))}
                data-testid="edit-feedback-name"
              />
            </div>

            <div>
              <Label>Review Date</Label>
              <Input 
                type="date"
                value={editingReview.review_date || ''} 
                onChange={e => setEditingReview(prev => ({ ...prev, review_date: e.target.value }))}
                data-testid="edit-feedback-date"
              />
            </div>
            
            {[
              { key: 'overall_rating', label: 'Overall Rating' },
              { key: 'quality_rating', label: 'Quality Rating' },
              { key: 'communication_rating', label: 'Communication Rating' },
              { key: 'timeliness_rating', label: 'Timeliness Rating' }
            ].map(({ key, label }) => (
              <div key={key}>
                <Label>{label}</Label>
                <div className="flex items-center gap-1 mt-1">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEditingReview(prev => ({ ...prev, [key]: star }))}
                      className="p-0.5"
                    >
                      <Star className={`h-6 w-6 cursor-pointer transition-colors ${star <= editingReview[key] ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} />
                    </button>
                  ))}
                  <span className="text-sm text-muted-foreground ml-2">({editingReview[key]}/5)</span>
                </div>
              </div>
            ))}

            <div>
              <Label>Comments</Label>
              <Textarea 
                value={editingReview.comments} 
                onChange={e => setEditingReview(prev => ({ ...prev, comments: e.target.value }))}
                rows={3}
                data-testid="edit-feedback-comments"
              />
            </div>

            <div>
              <Label>Improvement Suggestions</Label>
              <Textarea 
                value={editingReview.improvement_suggestions} 
                onChange={e => setEditingReview(prev => ({ ...prev, improvement_suggestions: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={editingReview.recommend} 
                onChange={e => setEditingReview(prev => ({ ...prev, recommend: e.target.checked }))}
                id="edit-recommend"
                className="rounded"
              />
              <Label htmlFor="edit-recommend">Would recommend</Label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingReview(null); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} data-testid="edit-feedback-save">
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Create Manual Review Dialog */}
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Manual Review</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Customer Name *</Label>
            <Input 
              value={newReview.customer_name} 
              onChange={e => setNewReview(prev => ({ ...prev, customer_name: e.target.value }))}
              placeholder="e.g. John Smith"
              data-testid="create-review-name"
            />
          </div>

          <div>
            <Label>Review Date</Label>
            <Input 
              type="date"
              value={newReview.review_date || ''} 
              onChange={e => setNewReview(prev => ({ ...prev, review_date: e.target.value }))}
              data-testid="create-review-date"
            />
          </div>

          <div>
            <Label>Source</Label>
            <select 
              value={newReview.source} 
              onChange={e => setNewReview(prev => ({ ...prev, source: e.target.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="create-review-source"
            >
              <option value="Manual">Manual</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="website">Website</option>
            </select>
          </div>

          <div>
            <Label>Overall Rating</Label>
            <div className="flex items-center gap-1 mt-1">
              {[1,2,3,4,5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setNewReview(prev => ({ ...prev, overall_rating: star }))}
                  className="p-0.5"
                >
                  <Star className={`h-6 w-6 cursor-pointer transition-colors ${star <= newReview.overall_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} />
                </button>
              ))}
              <span className="text-sm text-muted-foreground ml-2">({newReview.overall_rating}/5)</span>
            </div>
          </div>

          <div>
            <Label>Review Text</Label>
            <Textarea 
              value={newReview.comments} 
              onChange={e => setNewReview(prev => ({ ...prev, comments: e.target.value }))}
              rows={4}
              placeholder="What did the customer say?"
              data-testid="create-review-comments"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateReview} 
              disabled={isCreating || !newReview.customer_name.trim()}
              data-testid="create-review-submit"
            >
              {isCreating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add Review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default FeedbackTab;
